import BigNumber from 'bignumber.js';

import { OpenPosition } from '@flipster/r230522/original/position';
import Num from '@flipster/utils/num';
import { V2OrderSideType } from '@flipster/utils/types/base';

/**
 * entry price 대비 가격의 변동률(%)을 계산한다. (TP/SL "from entry" 표기용)
 * 반환값은 퍼센트 수치 문자열(예: +10% → '10'). 미입력('')·NaN·0 이하 값은 모두 '0'.
 * @example calcFromEntryPercentage('110', '100') // '10'
 */
export const calcFromEntryPercentage = (price: string, entryPrice: string): string => {
  const entry = Num(entryPrice);
  const target = Num(price);
  if (!entry.isGreaterThan(0) || !target.isGreaterThan(0)) return '0';
  return target.dividedBy(entry).multipliedBy(100).minus(100).toString();
};

// https://prextech.slack.com/archives/C04407SABK4/p1712891134344389?thread_ts=1712802588.341749&cid=C04407SABK4
// unrealizedPnl = (trigger price - entry price) * position
// fundsInvested = isolatedMarginInitial + isolatedMarginAdded
// unrealizedPnlPct = unrealizedPnl / fundsInvested * 100
export const calcPositionEstUnrealizedPnl = (position: OpenPosition | undefined, triggerPrice: string | undefined) => {
  if (!position || !triggerPrice) return { estUnrealizedPnl: '0', estUnrealizedPnlPct: '0' };
  const estUnrealizedPnl = Num(triggerPrice).minus(position?.avgEntryPrice).multipliedBy(position.position);
  const estUnrealizedPnlPct = estUnrealizedPnl.dividedBy(position.fundsInvested).multipliedBy(100).toString();
  return {
    estUnrealizedPnl: estUnrealizedPnl.toString(),
    estUnrealizedPnlPct,
  };
};

// https://prextech.slack.com/archives/C04407SABK4/p1712225428290199?thread_ts=1712218125.194519&cid=C04407SABK4
// orderSide = 1 if long order, -1 if short order
// estUnrealizedPnl = (trigger − entry) × amount / entry × orderSide
// Multiply before divide so repeating ratios (e.g. 7/13) do not leave 34999.999… dust
// that truncates to 34999.99 under Formatted.Number no-round + toFixed(2).
// estUnrealizedPnlPct = estUnrealizedPnl / (amount / leverage) * 100
export const calcOrderEstUnrealizedPnl = (
  side: V2OrderSideType,
  entryPrice: string,
  triggerPrice: string | undefined,
  amount: string,
  leverage: number,
) => {
  if (!triggerPrice || Num(triggerPrice).isZero()) return { estUnrealizedPnl: '0', estUnrealizedPnlPct: '0' };
  const estUnrealizedPnl = Num(triggerPrice)
    .minus(entryPrice)
    .multipliedBy(amount)
    .dividedBy(entryPrice)
    .multipliedBy(side === 'Long' ? 1 : -1);
  const estUnrealizedPnlPct = estUnrealizedPnl.dividedBy(Num(amount).dividedBy(leverage)).multipliedBy(100).toString();

  return {
    estUnrealizedPnl: estUnrealizedPnl.toString(),
    estUnrealizedPnlPct,
  };
};

export const calcOrderEstLiqPrice = (
  side: V2OrderSideType,
  orderPrice: string,
  leverage: number,
  maintenanceMarginRate: string,
  pricePrecision: number,
) => {
  // orderSide = -1 if long order, 1 if short order
  // price * (1 + (1 / leverage * orderSide) / (1 + maintenanceMarginRate * orderSide)
  // long 올림, short 내림
  const isLong = side === 'Long';
  const orderSide = isLong ? -1 : 1;
  const liqPrice = Num(orderPrice)
    .multipliedBy(Num(1).plus(Num(1).dividedBy(leverage).multipliedBy(orderSide)))
    .dividedBy(Num(1).plus(Num(maintenanceMarginRate).multipliedBy(orderSide)))
    .toFixed(pricePrecision, isLong ? BigNumber.ROUND_UP : BigNumber.ROUND_DOWN);
  return BigNumber.max(0, liqPrice).toString();
};
